import type { EndpointsRegistry, FetchClient } from '~/api/types'
import { GatewayEndpoints } from '~/api/endpoints/gateway-endpoints'
import type { SuccessNoContent } from '~/types/common'
import type { GroupsService } from '~/api/services/types'
import type {
  BehaviourCreatePayload,
  Group,
  GroupAsset,
  GroupAssetPresetState,
  GroupAssetsQueryParams,
  GroupBehaviour,
  GroupDetails
} from '~/types/group'
import type { GroupQueryParams, PaginatedResult } from '~/types/pagination'
import type { PresetValues } from '~/types/asset-configuration'
import type { GroupPresetStatus, GroupPresetSyncResult } from '~/types/configurationPreset'
import { isGroupPresetMocked } from '~/composables/useFeatureFlags'

interface GroupBehaviourScheduleResponse {
  id: number
  name: string
}

interface GroupBehaviourPresetResponse {
  id: number
  name: string
  values?: PresetValues
}

// Also embedded on GET /schedules/{id}, mapped by the schedules service.
export interface GroupBehaviourResponse {
  id: number
  target_id: number
  target_type: string
  target_asset_type: string
  schedule?: GroupBehaviourScheduleResponse
  preset?: GroupBehaviourPresetResponse
}

interface GroupAssetResponse {
  id: number
  name: string
  assetTypeId: string
  serialNumber: string
  preset_state?: GroupAssetPresetState | null
  preset_name?: string | null
  preset_note?: string | null
}

const mapGroupAsset = (asset: GroupAssetResponse): GroupAsset => ({
  id: asset.id,
  name: asset.name,
  assetTypeId: asset.assetTypeId,
  serialNumber: asset.serialNumber,
  ...(asset.preset_state && { presetState: asset.preset_state }),
  ...(asset.preset_name && { presetName: asset.preset_name }),
  ...(asset.preset_note && { presetNote: asset.preset_note })
})

interface GroupPresetStatusResponse {
  asset_type: string
  synced?: number
  in_progress?: number
  out_of_sync?: number
  unknown?: number
}

interface GroupPresetSyncResponse {
  sent?: number
  skipped?: number
  invalid_action_keys?: string[]
}

interface GroupDetailsResponse {
  id: number
  name: string
  site_id?: number | null
  asset_types?: string[]
  behaviours: GroupBehaviourResponse[]
}

interface GroupResponse {
  id: number
  name: string
  site_id?: number | null
  schedule_ids?: number[]
  asset_types?: string[]
  behaviours?: GroupBehaviourResponse[]
}

const mapGroup = (group: GroupResponse): Group => ({
  id: group.id,
  name: group.name,
  ...(group.site_id !== undefined && { siteId: group.site_id }),
  ...(group.schedule_ids && { scheduleIds: group.schedule_ids }),
  ...(group.asset_types && { assetTypes: group.asset_types }),
  ...(group.behaviours && { behaviours: group.behaviours.map(mapBehaviour) })
})

const toGroupPayload = ({
  siteId,
  ...group
}: Partial<Group>): Omit<Partial<Group>, 'siteId'> & { site_id?: number } => ({
  ...group,
  ...(siteId != null && { site_id: siteId })
})

export const mapBehaviour = (behaviour: GroupBehaviourResponse): GroupBehaviour => ({
  id: behaviour.id,
  targetId: behaviour.target_id,
  targetType: behaviour.target_type,
  targetAssetType: behaviour.target_asset_type,
  ...(behaviour.schedule && {
    schedule: { id: behaviour.schedule.id, name: behaviour.schedule.name }
  }),
  ...(behaviour.preset && {
    preset: { id: behaviour.preset.id, name: behaviour.preset.name, values: behaviour.preset.values ?? {} }
  })
})

export const registerEndpoints = (client: FetchClient, registry: EndpointsRegistry): GroupsService => {
  return {
    getAllGroups: async (assetTypeIds?: string[], siteId?: number) => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.GROUPS_GET_ALL, {
        queryParams: {
          ...(assetTypeIds?.length && { asset_type_ids: assetTypeIds }),
          ...(siteId && { site_id: siteId })
        }
      })
      const result = await client<PaginatedResult<GroupResponse>>(url, { method, headers, body })
      return result.data.map(mapGroup)
    },

    getGroupsPaginated: async (pagination: GroupQueryParams) => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.GROUPS_GET_ALL, {
        queryParams: {
          current_page: pagination.current_page,
          per_page: pagination.per_page,
          ...(pagination.sort && { sort: pagination.sort }),
          ...(pagination.search_terms && { search_terms: pagination.search_terms }),
          ...(pagination.site_id && { site_id: pagination.site_id })
        }
      })
      const result = await client<PaginatedResult<GroupResponse>>(url, { method, headers, body })
      return {
        data: result.data.map(mapGroup),
        pagination: result.pagination
      }
    },

    createGroup: async (groupData: Partial<Group>): Promise<Group> => {
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.GROUPS_CREATE,
        {},
        toGroupPayload(groupData)
      )
      return mapGroup(await client<GroupResponse>(url, { method, headers, body }))
    },

    updateGroup: async (id: number, groupData: Partial<Group>) => {
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.GROUPS_UPDATE,
        { params: { id } },
        groupData
      )
      return await client<Group>(url, { method, headers, body })
    },

    deleteGroup: async (id: number) => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.GROUPS_DELETE, { params: { id } })
      return await client<SuccessNoContent>(url, { method, headers, body })
    },

    assignAssetsToGroup: async (id: number, assetIds: number[]) => {
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.GROUPS_ASSIGN_ASSETS,
        { params: { id } },
        { assetIds }
      )
      return await client<SuccessNoContent>(url, { method, headers, body })
    },

    unassignAssetsFromGroup: async (id: number, assetIds: number[]) => {
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.GROUPS_UNASSIGN_ASSETS,
        { params: { id } },
        { assetIds }
      )
      return await client<SuccessNoContent>(url, { method, headers, body })
    },

    getAssetsByGroupId: async (id: number, pagination: GroupAssetsQueryParams) => {
      const fetchPage = async (params: GroupAssetsQueryParams): Promise<PaginatedResult<GroupAsset>> => {
        const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.GROUPS_GET_ASSETS, {
          params: { id },
          queryParams: {
            current_page: params.current_page,
            per_page: params.per_page,
            ...(params.sort && { sort: params.sort }),
            ...(params.preset_state?.length && { preset_state: params.preset_state.join(',') })
          }
        })
        const result = await client<PaginatedResult<GroupAssetResponse>>(url, { method, headers, body })
        return { data: result.data.map(mapGroupAsset), pagination: result.pagination }
      }
      if (isGroupPresetMocked) {
        const mock = await import('~/mocks/groupPresetMock')
        return mock.decorateGroupAssets(id, pagination, fetchPage)
      }
      return fetchPage(pagination)
    },

    getGroupDetails: async (id: number): Promise<GroupDetails> => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.GROUPS_GET_DETAILS, {
        params: { id }
      })
      const data = await client<GroupDetailsResponse>(url, { method, headers, body })
      const details: GroupDetails = {
        id: data.id,
        name: data.name,
        ...(data.site_id !== undefined && { siteId: data.site_id }),
        assetTypes: data.asset_types ?? [],
        behaviours: (data.behaviours ?? []).map(mapBehaviour)
      }
      if (isGroupPresetMocked) {
        const mock = await import('~/mocks/groupPresetMock')
        return mock.decorateGroupDetails(details)
      }
      return details
    },

    createBehaviour: async (payload: BehaviourCreatePayload): Promise<GroupBehaviour> => {
      if (isGroupPresetMocked && payload.scheduleId === undefined) {
        const mock = await import('~/mocks/groupPresetMock')
        return mock.createPresetBehaviour(payload)
      }
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.BEHAVIOURS_CREATE,
        { queryParams: payload.overwrite ? { overwrite: true } : {} },
        {
          target_id: payload.groupId,
          target_type: 'group',
          target_asset_type: payload.assetType,
          ...(payload.scheduleId !== undefined && { schedule_id: payload.scheduleId }),
          ...(payload.presetId !== undefined && { preset_id: payload.presetId })
        }
      )
      const data = await client<GroupBehaviourResponse>(url, { method, headers, body })
      return mapBehaviour(data)
    },

    removeBehaviourSchedule: async (id: number) => {
      if (isGroupPresetMocked) {
        const mock = await import('~/mocks/groupPresetMock')
        mock.removeBehaviourSchedule(id)
        if (mock.isSyntheticBehaviour(id)) return {} as SuccessNoContent
      }
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.BEHAVIOURS_REMOVE_SCHEDULE, {
        params: { id }
      })
      return await client<SuccessNoContent>(url, { method, headers, body })
    },

    removeBehaviourPreset: async (behaviourId: number) => {
      if (isGroupPresetMocked) {
        const mock = await import('~/mocks/groupPresetMock')
        mock.removeBehaviourPreset(behaviourId)
        return {} as SuccessNoContent
      }
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.BEHAVIOURS_REMOVE_PRESET, {
        params: { id: behaviourId }
      })
      return await client<SuccessNoContent>(url, { method, headers, body })
    },

    getGroupPresetStatus: async (groupId: number): Promise<GroupPresetStatus[]> => {
      if (isGroupPresetMocked) {
        const mock = await import('~/mocks/groupPresetMock')
        return mock.getGroupPresetStatus(groupId)
      }
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.GROUPS_GET_PRESET_STATUS, {
        params: { id: groupId }
      })
      const data = await client<GroupPresetStatusResponse[] | null>(url, { method, headers, body })
      return (data ?? []).map((status) => ({
        assetType: status.asset_type,
        synced: status.synced ?? 0,
        inProgress: status.in_progress ?? 0,
        outOfSync: status.out_of_sync ?? 0,
        unknown: status.unknown ?? 0
      }))
    },

    syncGroupPresets: async (groupId: number, assetType: string, force = false): Promise<GroupPresetSyncResult> => {
      if (isGroupPresetMocked) {
        const mock = await import('~/mocks/groupPresetMock')
        return mock.syncGroupPresets(groupId, assetType)
      }
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.GROUPS_SYNC_PRESETS,
        { params: { id: groupId } },
        { asset_type: assetType, ...(force && { force }) }
      )
      const data = await client<GroupPresetSyncResponse | null>(url, { method, headers, body })
      return {
        sent: data?.sent ?? 0,
        skipped: data?.skipped ?? 0,
        ...(data?.invalid_action_keys?.length && { invalidActionKeys: data.invalid_action_keys })
      }
    }
  }
}
